import React, { useState, useMemo, useEffect } from 'react';
import { api } from '../services/client';
import { ConfirmDialog } from '../components/ui/Modal';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Scan,
  Search,
  Filter,
  Tag,
  Eye,
  Copy,
  Download,
  Share2,
  Trash2,
  Settings,
  ChevronRight,
  ChevronDown,
  Plus,
  X,
  Link,
  FileText,
  Bookmark,
  Calendar,
  User,
  Clock,
  MapPin,
  DollarSign,
  Hash,
  Type,
  List,
  Check,
  AlertCircle,
  Loader2,
} from 'lucide-react';

// Types
type EntityType = 'person' | 'organization' | 'location' | 'date' | 'email' | 'url' | 'money' | 'hashtag';

interface ExtractedEntity {
  id: string;
  type: EntityType;
  value: string;
  confidence: number;
  source: {
    type: 'tab' | 'session' | 'bookmark' | 'research';
    id: string;
    title: string;
  };
  context: string;
  tags: string[];
  createdAt: Date;
  metadata?: Record<string, unknown>;
}

interface ExtractionRule {
  id: string;
  name: string;
  type: EntityType;
  pattern: string;
  isActive: boolean;
  count: number;
}

interface EntityPattern {
  id: string;
  ruleId: string;
  name: string;
  type: EntityType;
  pattern: string;
  isActive: boolean;
  matchCount: number;
  uniqueValues: number;
  sampleValues: string[];
}

function toEntityType(value: unknown): EntityType {
  switch (String(value ?? '')) {
    case 'person':
    case 'organization':
    case 'location':
    case 'date':
    case 'email':
    case 'url':
    case 'money':
    case 'hashtag':
      return value as EntityType;
    default:
      return 'person';
  }
}

function mapApiEntity(raw: Record<string, unknown>): ExtractedEntity {
  const source = (raw.source as Record<string, unknown>) ?? {};
  return {
    id: String(raw.id ?? ''),
    type: toEntityType(raw.type),
    value: String(raw.value ?? ''),
    confidence: typeof raw.confidence === 'number' ? raw.confidence : 0.85,
    source: {
      type: (source.type ?? raw.source_type ?? 'tab') as ExtractedEntity['source']['type'],
      id: String(source.id ?? raw.source_id ?? ''),
      title: String(source.title ?? raw.source_title ?? 'Unknown source'),
    },
    context: String(raw.context ?? ''),
    tags: Array.isArray(raw.tags) ? raw.tags.map(String) : [],
    createdAt: raw.created_at ? new Date(String(raw.created_at)) : new Date(),
    metadata: raw.metadata as Record<string, unknown> | undefined,
  };
}

function mapApiRule(raw: Record<string, unknown>): ExtractionRule {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    type: toEntityType(raw.type),
    pattern: String(raw.pattern ?? ''),
    isActive: raw.is_active !== false,
    count: typeof raw.count === 'number' ? raw.count : 0,
  };
}

function mapApiPattern(raw: Record<string, unknown>): EntityPattern {
  return {
    id: String(raw.id ?? ''),
    ruleId: String(raw.rule_id ?? raw.id ?? ''),
    name: String(raw.name ?? ''),
    type: toEntityType(raw.type),
    pattern: String(raw.pattern ?? ''),
    isActive: raw.is_active !== false,
    matchCount: typeof raw.match_count === 'number' ? raw.match_count : 0,
    uniqueValues: typeof raw.unique_values === 'number' ? raw.unique_values : 0,
    sampleValues: Array.isArray(raw.sample_values) ? raw.sample_values.map(String) : [],
  };
}

export const EntityExtractionPage: React.FC = () => {
  // State
  const [entities, setEntities] = useState<ExtractedEntity[]>([]);
  const [rules, setRules] = useState<ExtractionRule[]>([]);
  const [patterns, setPatterns] = useState<EntityPattern[]>([]);
  const [activeTab, setActiveTab] = useState<'entities' | 'rules' | 'patterns'>('entities');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<EntityType | 'all'>('all');
  const [selectedEntity, setSelectedEntity] = useState<ExtractedEntity | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [extractionMessage, setExtractionMessage] = useState<string | null>(null);
  const [expandedTypes, setExpandedTypes] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [entitiesRes, rulesRes, patternsRes] = await Promise.all([
        api.get('/entity-extraction/entities'),
        api.get('/entity-extraction/rules'),
        api.get('/entity-extraction/patterns'),
      ]);
      const rawEntities = entitiesRes.data?.entities ?? [];
      const rawRules = rulesRes.data?.rules ?? [];
      const rawPatterns = patternsRes.data?.patterns ?? [];
      setEntities(rawEntities.map((e: Record<string, unknown>) => mapApiEntity(e)));
      setRules(rawRules.map((r: Record<string, unknown>) => mapApiRule(r)));
      setPatterns(rawPatterns.map((p: Record<string, unknown>) => mapApiPattern(p)));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter entities
  const filteredEntities = useMemo(() => {
    let result = entities;

    if (filterType !== 'all') {
      result = result.filter(e => e.type === filterType);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(e =>
        e.value.toLowerCase().includes(query) ||
        e.context.toLowerCase().includes(query) ||
        e.tags.some(t => t.toLowerCase().includes(query))
      );
    }

    return result;
  }, [entities, filterType, searchQuery]);

  // Group entities by type
  const groupedEntities = useMemo(() => {
    const groups: Record<string, ExtractedEntity[]> = {};
    filteredEntities.forEach(entity => {
      if (!groups[entity.type]) {
        groups[entity.type] = [];
      }
      groups[entity.type].push(entity);
    });
    return groups;
  }, [filteredEntities]);

  // Toggle type expansion
  const toggleType = (type: string) => {
    setExpandedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  // Get entity icon
  const getEntityIcon = (type: ExtractedEntity['type']) => {
    switch (type) {
      case 'person': return <User size={14} />;
      case 'organization': return <Link size={14} />;
      case 'location': return <MapPin size={14} />;
      case 'date': return <Calendar size={14} />;
      case 'email': return <Type size={14} />;
      case 'url': return <Link size={14} />;
      case 'money': return <DollarSign size={14} />;
      case 'hashtag': return <Hash size={14} />;
      default: return <Tag size={14} />;
    }
  };

  // Get type color
  const getTypeColor = (type: ExtractedEntity['type']) => {
    switch (type) {
      case 'person': return 'text-primary-400 bg-primary-500/20';
      case 'organization': return 'text-purple-400 bg-purple-500/20';
      case 'location': return 'text-green-400 bg-green-500/20';
      case 'date': return 'text-yellow-400 bg-yellow-500/20';
      case 'email': return 'text-cyan-400 bg-cyan-500/20';
      case 'url': return 'text-indigo-400 bg-indigo-500/20';
      case 'money': return 'text-emerald-400 bg-emerald-500/20';
      case 'hashtag': return 'text-pink-400 bg-pink-500/20';
      default: return 'text-khiimori-text-muted bg-khiimori-bg-tertiary/20';
    }
  };

  const toggleRule = async (ruleId: string) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    const nextActive = !rule.isActive;
    setRules(rules.map(r => (r.id === ruleId ? { ...r, isActive: nextActive } : r)));
    try {
      await api.put(`/entity-extraction/rules/${ruleId}`, { is_active: nextActive });
      const patternsRes = await api.get('/entity-extraction/patterns');
      const rawPatterns = patternsRes.data?.patterns ?? [];
      setPatterns(rawPatterns.map((p: Record<string, unknown>) => mapApiPattern(p)));
    } catch (err) {
      console.error(err);
      setRules(rules.map(r => (r.id === ruleId ? { ...r, isActive: rule.isActive } : r)));
    }
  };

  // Delete entity
  const deleteEntity = (entityId: string) => {
    setSelectedEntityId(entityId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteEntity = async () => {
    if (!selectedEntityId) return;
    try {
      await api.delete(`/entity-extraction/entities/${selectedEntityId}`);
      setEntities(entities.filter(e => e.id !== selectedEntityId));
      if (selectedEntity?.id === selectedEntityId) {
        setSelectedEntity(null);
        setShowPreview(false);
      }
      const [rulesRes, patternsRes] = await Promise.all([
        api.get('/entity-extraction/rules'),
        api.get('/entity-extraction/patterns'),
      ]);
      setRules((rulesRes.data?.rules ?? []).map((r: Record<string, unknown>) => mapApiRule(r)));
      setPatterns((patternsRes.data?.patterns ?? []).map((p: Record<string, unknown>) => mapApiPattern(p)));
    } catch (err) {
      console.error(err);
    } finally {
      setShowDeleteConfirm(false);
      setSelectedEntityId(null);
    }
  };

  // Copy entity value
  const copyValue = (value: string) => {
    navigator.clipboard.writeText(value);
  };

  const runExtraction = async () => {
    setIsExtracting(true);
    setExtractionMessage('Fetching page content and extracting entities — this may take up to a minute…');
    try {
      const res = await api.post('/entity-extraction/run', {}, { timeout: 90000 });
      const rawEntities = res.data?.entities ?? [];
      const mapped = rawEntities.map((e: Record<string, unknown>) => mapApiEntity(e));
      setEntities(mapped);
      setExpandedTypes([...new Set(mapped.map((e) => e.type))]);
      if (res.data?.message) {
        setExtractionMessage(String(res.data.message));
      } else if (typeof res.data?.new_count === 'number') {
        const scanned = typeof res.data?.tabs_scanned === 'number' ? res.data.tabs_scanned : null;
        const fetched = typeof res.data?.tabs_fetched === 'number' ? res.data.tabs_fetched : null;
        const scanNote = scanned != null ? ` across ${scanned} tab${scanned !== 1 ? 's' : ''}` : '';
        const fetchNote = fetched != null && scanned != null ? ` (${fetched}/${scanned} pages fetched)` : '';
        setExtractionMessage(
          res.data.new_count > 0
            ? `Found ${res.data.new_count} new entities (${res.data.total_count ?? rawEntities.length} total)${scanNote}${fetchNote}`
            : `Scan complete — ${res.data.total_count ?? rawEntities.length} entities on file${scanNote}${fetchNote}`
        );
      }
      const [rulesRes, patternsRes] = await Promise.all([
        api.get('/entity-extraction/rules'),
        api.get('/entity-extraction/patterns'),
      ]);
      setRules((rulesRes.data?.rules ?? []).map((r: Record<string, unknown>) => mapApiRule(r)));
      setPatterns((patternsRes.data?.patterns ?? []).map((p: Record<string, unknown>) => mapApiPattern(p)));
    } catch (err) {
      console.error(err);
      setExtractionMessage('Extraction failed. Check that you have open tabs in Live.');
    } finally {
      setIsExtracting(false);
    }
  };

  // Format timestamp
  const formatTimestamp = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="h-full flex flex-col bg-khiimori-bg-secondary">
      {/* Header */}
      <div className="border-b border-khiimori-border p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-khiimori-text-primary">Entity Extraction Viewer</h1>
            <p className="text-sm text-khiimori-text-muted mt-1">Automatically extract and view entities from your content</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={runExtraction}
              disabled={isExtracting}
              className="px-4 py-2 bg-khiimori-bg-elevated text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {isExtracting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Extracting...
                </>
              ) : (
                <>
                  <Scan size={16} />
                  Run Extraction
                </>
              )}
            </button>
            <button
              onClick={() => setActiveTab('rules')}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-400 transition-colors flex items-center gap-2"
            >
              <Settings size={16} />
              Manage Rules
            </button>
          </div>
        </div>

        {/* Search and filters */}
        <div className="mt-4 flex items-center gap-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-khiimori-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search entities..."
              className="w-full pl-10 pr-4 py-2 bg-khiimori-bg-elevated border border-khiimori-border rounded-lg text-khiimori-text-primary placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-4 py-2 bg-khiimori-bg-elevated border border-khiimori-border rounded-lg text-khiimori-text-primary focus:outline-none focus:border-purple-500"
          >
            <option value="all">All Types</option>
            <option value="person">People</option>
            <option value="organization">Organizations</option>
            <option value="location">Locations</option>
            <option value="date">Dates</option>
            <option value="email">Emails</option>
            <option value="url">URLs</option>
            <option value="money">Money</option>
            <option value="hashtag">Hashtags</option>
          </select>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-4 mt-4 border-b border-khiimori-border">
          {[
            { id: 'entities', label: 'Extracted Entities', count: entities.length },
            { id: 'rules', label: 'Extraction Rules', count: rules.length },
            { id: 'patterns', label: 'Patterns', count: patterns.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`pb-3 px-1 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'text-purple-400 border-b-2 border-purple-400'
                  : 'text-khiimori-text-muted hover:text-khiimori-text-secondary'
              }`}
            >
              {tab.label}
              <span className="bg-khiimori-bg-elevated text-khiimori-text-muted text-xs px-2 py-0.5 rounded-full">{tab.count}</span>
            </button>
          ))}
        </div>
        {extractionMessage && (
          <p className="mt-3 text-sm text-khiimori-text-muted flex items-center gap-2">
            <AlertCircle size={14} />
            {extractionMessage}
          </p>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-khiimori-text-muted gap-2">
            <Loader2 size={20} className="animate-spin" />
            Loading entities…
          </div>
        ) : (
        <AnimatePresence mode="wait">
          {activeTab === 'entities' && (
            <motion.div
              key="entities"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {Object.keys(groupedEntities).length === 0 ? (
                <div className="text-center py-12">
                  <Scan size={48} className="mx-auto text-khiimori-text-muted mb-4" />
                  <h3 className="text-lg font-medium text-khiimori-text-secondary">No entities found</h3>
                  <p className="text-khiimori-text-muted mt-2">
                    Run extraction to scan your open tabs, or adjust your search filters
                  </p>
                  <button
                    onClick={runExtraction}
                    disabled={isExtracting}
                    className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-400 transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    {isExtracting ? <Loader2 size={16} className="animate-spin" /> : <Scan size={16} />}
                    Run Extraction
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedEntities).map(([type, typeEntities]) => (
                    <div key={type} className="bg-khiimori-bg-elevated rounded-xl overflow-hidden">
                      <div
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-khiimori-bg-elevated transition-colors"
                        onClick={() => toggleType(type)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${getTypeColor(type as ExtractedEntity['type'])}`}>
                            {getEntityIcon(type as ExtractedEntity['type'])}
                          </div>
                          <div>
                            <h3 className="font-semibold text-khiimori-text-primary capitalize">{type}s</h3>
                            <span className="text-sm text-khiimori-text-muted">{typeEntities.length} entities</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {expandedTypes.includes(type) ? (
                            <ChevronDown size={18} className="text-khiimori-text-muted" />
                          ) : (
                            <ChevronRight size={18} className="text-khiimori-text-muted" />
                          )}
                        </div>
                      </div>

                      <AnimatePresence>
                        {expandedTypes.includes(type) && (
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 'auto' }}
                            exit={{ height: 0 }}
                            className="border-t border-khiimori-border"
                          >
                            {typeEntities.map(entity => (
                              <div
                                key={entity.id}
                                className="p-4 border-b border-khiimori-border/50 last:border-b-0 hover:bg-khiimori-bg-elevated transition-colors cursor-pointer"
                                onClick={() => { setSelectedEntity(entity); setShowPreview(true); }}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-khiimori-text-primary">{entity.value}</span>
                                      {entity.confidence < 0.75 || entity.metadata?.tentative ? (
                                        <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                                          Tentative
                                        </span>
                                      ) : null}
                                      <span className="text-xs bg-khiimori-bg-tertiary text-khiimori-text-muted px-2 py-0.5 rounded">
                                        {Math.round(entity.confidence * 100)}% confidence
                                      </span>
                                    </div>
                                    <p className="text-sm text-khiimori-text-muted mt-1 line-clamp-1">{entity.context}</p>
                                    <div className="flex items-center gap-3 mt-2 text-xs text-khiimori-text-muted">
                                      <span className="flex items-center gap-1">
                                        <Clock size={10} />
                                        {formatTimestamp(entity.createdAt)}
                                      </span>
                                      <span className="text-purple-400">{entity.source.title}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); copyValue(entity.value); }}
                                      className="p-1.5 hover:bg-khiimori-bg-tertiary rounded transition-colors"
                                      title="Copy value"
                                    >
                                      <Copy size={14} className="text-khiimori-text-muted" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); deleteEntity(entity.id); }}
                                      className="p-1.5 hover:bg-khiimori-bg-tertiary rounded transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 size={14} className="text-red-400" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'rules' && (
            <motion.div
              key="rules"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="space-y-3">
                {rules.map(rule => (
                  <div
                    key={rule.id}
                    className={`bg-khiimori-bg-elevated rounded-xl p-4 border transition-colors ${
                      rule.isActive ? 'border-khiimori-border' : 'border-khiimori-border opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${getTypeColor(rule.type)}`}>
                          {getEntityIcon(rule.type)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-khiimori-text-primary">{rule.name}</h3>
                          <code className="text-xs text-khiimori-text-muted bg-khiimori-bg-secondary px-2 py-0.5 rounded mt-1 block max-w-md truncate">
                            {rule.pattern}
                          </code>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-khiimori-text-muted">{rule.count} extractions</span>
                        <button
                          onClick={() => toggleRule(rule.id)}
                          className={`w-12 h-6 rounded-full transition-colors relative ${
                            rule.isActive ? 'bg-primary-500' : 'bg-khiimori-bg-tertiary'
                          }`}
                        >
                          <span
                            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                              rule.isActive ? 'left-7' : 'left-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {activeTab === 'patterns' && (
            <motion.div
              key="patterns"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {patterns.length === 0 ? (
                <div className="text-center py-12">
                  <List size={48} className="mx-auto text-khiimori-text-muted mb-4" />
                  <h3 className="text-lg font-medium text-khiimori-text-secondary">No patterns yet</h3>
                  <p className="text-khiimori-text-muted mt-2">Run extraction to populate pattern statistics from your tabs</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {patterns.map(pattern => (
                    <div
                      key={pattern.id}
                      className={`bg-khiimori-bg-elevated rounded-xl p-4 border transition-colors ${
                        pattern.isActive ? 'border-khiimori-border' : 'border-khiimori-border opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 min-w-0">
                          <div className={`p-2 rounded-lg shrink-0 ${getTypeColor(pattern.type)}`}>
                            {getEntityIcon(pattern.type)}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-khiimori-text-primary">{pattern.name}</h3>
                            <code className="text-xs text-khiimori-text-muted bg-khiimori-bg-secondary px-2 py-0.5 rounded mt-1 block truncate">
                              {pattern.pattern}
                            </code>
                            {pattern.sampleValues.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {pattern.sampleValues.map(sample => (
                                  <span
                                    key={sample}
                                    className="text-xs bg-khiimori-bg-tertiary text-khiimori-text-secondary px-2 py-0.5 rounded"
                                  >
                                    {sample}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-medium text-khiimori-text-primary">{pattern.matchCount} matches</div>
                          <div className="text-xs text-khiimori-text-muted">{pattern.uniqueValues} unique values</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        )}
      </div>

      {/* Preview Panel */}
      <AnimatePresence>
        {showPreview && selectedEntity && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            className="w-96 border-l border-khiimori-border bg-khiimori-bg-secondary flex flex-col"
          >
            <div className="p-4 border-b border-khiimori-border flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-khiimori-text-primary">Entity Details</h3>
                <span className={`text-xs capitalize ${getTypeColor(selectedEntity.type)} px-2 py-0.5 rounded`}>
                  {selectedEntity.type}
                </span>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-khiimori-bg-elevated rounded-lg transition-colors"
              >
                <X size={18} className="text-khiimori-text-muted" />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-auto space-y-4">
              {/* Value */}
              <div className="bg-khiimori-bg-elevated rounded-lg p-4">
                <h4 className="text-xs font-medium text-khiimori-text-muted mb-2">Value</h4>
                <div className="flex items-center gap-2">
                  <span className="text-lg text-khiimori-text-primary font-medium">{selectedEntity.value}</span>
                  <button
                    onClick={() => copyValue(selectedEntity.value)}
                    className="p-1 hover:bg-khiimori-bg-tertiary rounded transition-colors"
                  >
                    <Copy size={14} className="text-khiimori-text-muted" />
                  </button>
                </div>
              </div>

              {/* Confidence */}
              <div className="bg-khiimori-bg-elevated rounded-lg p-4">
                <h4 className="text-xs font-medium text-khiimori-text-muted mb-2">Confidence</h4>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-khiimori-bg-tertiary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500"
                      style={{ width: `${selectedEntity.confidence * 100}%` }}
                    />
                  </div>
                  <span className="text-khiimori-text-primary font-medium">{Math.round(selectedEntity.confidence * 100)}%</span>
                </div>
              </div>

              {/* Context */}
              <div className="bg-khiimori-bg-elevated rounded-lg p-4">
                <h4 className="text-xs font-medium text-khiimori-text-muted mb-2">Context</h4>
                <p className="text-sm text-khiimori-text-secondary">{selectedEntity.context}</p>
              </div>

              {/* Source */}
              <div className="bg-khiimori-bg-elevated rounded-lg p-4">
                <h4 className="text-xs font-medium text-khiimori-text-muted mb-2">Source</h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-khiimori-bg-tertiary text-khiimori-text-muted px-2 py-0.5 rounded capitalize">
                    {selectedEntity.source.type}
                  </span>
                  <span className="text-sm text-purple-400">{selectedEntity.source.title}</span>
                </div>
              </div>

              {/* Tags */}
              <div className="bg-khiimori-bg-elevated rounded-lg p-4">
                <h4 className="text-xs font-medium text-khiimori-text-muted mb-2">Tags</h4>
                <div className="flex flex-wrap gap-1">
                  {selectedEntity.tags.map(tag => (
                    <span key={tag} className="text-xs bg-khiimori-bg-tertiary text-khiimori-text-secondary px-2 py-0.5 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Metadata */}
              {selectedEntity.metadata && (
                <div className="bg-khiimori-bg-elevated rounded-lg p-4">
                  <h4 className="text-xs font-medium text-khiimori-text-muted mb-2">Metadata</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedEntity.metadata).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-khiimori-text-muted">{key}</span>
                        <span className="text-khiimori-text-primary">{value as string}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-khiimori-border flex gap-2">
              <button className="flex-1 py-2 bg-khiimori-bg-tertiary text-khiimori-text-primary rounded-lg hover:bg-khiimori-bg-tertiary transition-colors flex items-center justify-center gap-2">
                <Share2 size={14} />
                Share
              </button>
              <button
                onClick={() => deleteEntity(selectedEntity.id)}
                className="flex-1 py-2 bg-error-500/20 text-red-400 rounded-lg hover:bg-error-500/30 transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteEntity}
        title="Delete Entity"
        message="Are you sure you want to delete this entity? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};